"use client";
import React, { useState } from 'react';
import Link from 'next/link';

import { Mail } from 'lucide-react';
import AdSlotWrapper from './AdSlotWrapper';
import GameWidget from './games/GameWidget';
import ShoppingWidget from './ShoppingWidget';

const formatTimeAgo = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} hours ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return `1 day ago`;
  return `${diffInDays} days ago`;
};

const RightSidebar = ({ articles, settings = {} }) => {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribeStatus('loading');
    try {
      const response = await fetch('/api/newsletter/subscribers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (!response.ok) throw new Error('Subscription failed');
      setEmail('');
      setSubscribeStatus('success');
      setTimeout(() => setSubscribeStatus(''), 5000);
    } catch (err) {
      console.error(err);
      setSubscribeStatus('error');
    }
  };

  return (
    <aside className="w-full flex flex-col gap-8">
      
      {/* Top Vertical Ad Promotion (Skyscraper) */}
      <AdSlotWrapper settings={settings} id="homepage_sidebar_1" style={{ minHeight: '600px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded" />

      {/* Follow Us */}
      <div className="text-left w-full">
        <h3 className="text-xl font-bold mb-4 text-brand-text text-center">Follow Us:</h3>
        
        <div className="flex flex-col items-center gap-2">
          {/* Row 1 */}
          <div className="flex justify-center gap-2">
            <a href={settings?.socialLinks?.facebook || "#"} aria-label="Facebook" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-brand-secondary text-brand-text-inverse rounded-full flex items-center justify-center hover:bg-[var(--brand-surface-hover)] transition-colors">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M14 13.5h2.5l1-4H14v-2c0-1.03 0-2 2-2h1.5V2.14c-.326-.043-1.557-.14-2.857-.14C11.928 2 10 3.657 10 6.7v2.8H7v4h3V22h4v-8.5z"/></svg>
            </a>
            <a href={settings?.socialLinks?.twitter || "#"} aria-label="X (Twitter)" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-brand-secondary text-brand-text-inverse rounded-full flex items-center justify-center hover:bg-[var(--brand-surface-hover)] transition-colors">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
            </a>
            <a href={settings?.socialLinks?.threads || "#"} aria-label="Threads" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-brand-secondary text-brand-text-inverse rounded-full flex items-center justify-center hover:bg-[var(--brand-surface-hover)] transition-colors">
              <svg viewBox="0 0 192 192" fill="currentColor" className="w-5 h-5"><path d="M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 148.902 38.1079C156.738 47.5641 161.402 59.7891 162.778 74.0763L179.61 72.4402C177.962 55.4526 171.861 41.1396 161.854 29.1362C148.067 12.5539 126.335 3.51685 97.0135 3.31494C69.3176 3.51685 48.4357 12.336 33.7291 28.5303C19.7828 43.886 12.7533 66.5298 12.5113 96C12.7533 125.47 19.7828 148.114 33.7291 163.47C48.4357 179.664 69.3176 188.483 97.0135 188.685C123.633 188.483 141.745 181.714 154.542 168.932C172.483 151.01 172.937 128.583 166.494 113.626C162.067 103.35 154.024 95.0381 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C108.838 98.468 112.336 98.71 115.362 99.122C114.811 110.841 108.971 128.903 98.4405 129.507Z" /></svg>
            </a>
            <a href={settings?.socialLinks?.instagram || "#"} aria-label="Instagram" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-brand-secondary text-brand-text-inverse rounded-full flex items-center justify-center hover:bg-[var(--brand-surface-hover)] transition-colors">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
            </a>
          </div>
          {/* Row 2 */}
          <div className="flex justify-center gap-2">
            <a href={settings?.socialLinks?.bluesky || "#"} aria-label="Bluesky" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-brand-secondary text-brand-text-inverse rounded-full flex items-center justify-center hover:bg-[var(--brand-surface-hover)] transition-colors">
              <svg viewBox="0 0 320 286" fill="currentColor" className="w-4 h-4"><path d="M69.36 34.64C108.35 63.85 141.44 113.62 160 148.4C178.56 113.62 211.65 63.85 250.64 34.64C275.98 15.65 315.65-8.24 315.65 36.87C315.65 47.93 309.43 103.5 301.62 119.06C289.47 143.27 259.95 152.06 226.79 148.65C270.62 153.29 299.8 166.45 299.8 196.22C299.8 240.23 241.6 269.1 160 216.9C78.4 269.1 20.2 240.23 20.2 196.22C20.2 166.45 49.38 153.29 93.21 148.65C60.05 152.06 30.53 143.27 18.38 119.06C10.57 103.5 4.35 47.93 4.35 36.87C4.35-8.24 44.02 15.65 69.36 34.64Z"/></svg>
            </a>
            <a href={settings?.socialLinks?.linkedin || "#"} aria-label="LinkedIn" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-brand-secondary text-brand-text-inverse rounded-full flex items-center justify-center hover:bg-[var(--brand-surface-hover)] transition-colors">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
            </a>
            <Link href="/rss" aria-label="RSS Feed" className="w-10 h-10 bg-brand-secondary text-brand-text-inverse rounded-full flex items-center justify-center hover:bg-[var(--brand-surface-hover)] transition-colors">
              <svg viewBox="3 3 18 18" fill="currentColor" className="w-5 h-5">
                <path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z"/>
                <circle cx="6.18" cy="17.82" r="2.18"/>
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Square Ad After Follow Us */}
      <AdSlotWrapper settings={settings} id="homepage_sidebar_2" style={{ minHeight: '250px', marginBottom: '24px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded" />

      <div className="w-full bg-brand-surface border border-brand-border p-6 rounded-lg shadow-sm">
        <div className="flex items-center gap-2 mb-3 justify-center">
          <Mail className="w-6 h-6 text-brand-text" />
          <h3 className="text-xl font-bold text-brand-text tracking-tight">Newsletter</h3>
        </div>
        <p className="text-sm text-brand-muted mb-5 text-center leading-relaxed">
          Get the latest gadgets and tech news delivered straight to your inbox.
        </p>
        <form onSubmit={handleSubscribe} className="flex flex-col gap-3">
          <input 
            type="email" 
            placeholder="Your email address" 
            required 
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2.5 border border-[var(--brand-border)] rounded focus:outline-none focus:border-[var(--brand-primary)] focus:ring-1 focus:ring-[var(--brand-focus-ring)] text-sm text-brand-text placeholder-gray-500 bg-brand-surface transition-all"
          />
          <button 
            type="submit" 
            disabled={subscribeStatus === 'loading'}
            className="w-full bg-brand-secondary text-brand-text-inverse font-bold py-2.5 px-4 rounded hover:bg-[var(--brand-surface-hover)] transition-colors text-sm disabled:opacity-70 flex justify-center items-center"
          >
            {subscribeStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
          </button>
        </form>
        {subscribeStatus === 'success' && (
          <p className="text-green-600 text-sm mt-3 font-semibold text-center">Thanks for subscribing!</p>
        )}
        {subscribeStatus === 'error' && (
          <p className="text-red-600 text-sm mt-3 font-semibold text-center">Failed to subscribe. Please try again.</p>
        )}
      </div>

      {/* Ad Before Game Widget */}
      <AdSlotWrapper settings={settings} id="homepage_sidebar_3" style={{ minHeight: '250px', marginBottom: '24px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded" />

      {/* Game of the Day Widget */}
      <GameWidget />

      {/* Shopping Widget */}
      <div className="w-full mb-6">
        <ShoppingWidget settings={settings} />
      </div>

      {/* Ad Before Most Visited */}
      <AdSlotWrapper settings={settings} id="homepage_sidebar_4" style={{ minHeight: '250px', marginBottom: '24px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded" />

      {/* Most Visited Articles */}

      
      <div className="w-full bg-brand-surface mb-6">
        <h3 className="text-[20px] font-black uppercase tracking-tight text-brand-text border-b-2 border-gray-900 pb-2 mb-4">
          Most Visited
        </h3>
        <div className="flex flex-col gap-6">
          {(() => {
            const now = new Date();
            const fortyEightHoursAgo = new Date(now.getTime() - (48 * 60 * 60 * 1000));
            // Add fake views for sorting if they don't exist in DB
            const articlesWithViews = articles.map(a => {
               // Deterministic fake views based on article ID string length/characters
               const idHash = (a.id || a.slug || "0").split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
               const fakeViews = (idHash * 331) % 50000 + 1000;
               return {
                 ...a,
                 _dateToUse: a.date || a.createdAt || a.publishedAt || "2024-01-01T00:00:00Z",
                 _viewsToUse: a.views || fakeViews
               };
            });

            let mostReadArticles = [...articlesWithViews]
              .filter(article => new Date(article._dateToUse) >= fortyEightHoursAgo)
              .sort((a, b) => b._viewsToUse - a._viewsToUse)
              .slice(0, 5);

            // Fallback to all-time most read if we have less than 5 articles in the last 48 hours
            if (mostReadArticles.length < 5) {
               const fallbackArticles = [...articlesWithViews]
                 .filter(article => !mostReadArticles.some(m => m.id === article.id))
                 .sort((a, b) => b._viewsToUse - a._viewsToUse)
                 .slice(0, 5 - mostReadArticles.length);
               mostReadArticles.push(...fallbackArticles);
            }

            return mostReadArticles.map((article, index) => (
              <Link key={article.id} href={article.slug === '#' ? '#' : `/${article.slug}`} className="group flex gap-5 items-center">
                <span className="text-5xl font-black text-brand-text w-8 flex-shrink-0 group-hover:text-brand transition-colors">{index + 1}</span>
                <div className="w-[100px] h-[85px] flex-shrink-0 bg-gray-200">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex flex-col">
                  <h4 className="text-base font-bold leading-snug group-hover:text-brand transition-colors line-clamp-2">
                    {article.title}
                  </h4>
                  <p className="text-[10px] text-brand-muted font-bold mt-1 uppercase tracking-wider" suppressHydrationWarning>
                    {formatTimeAgo(article._dateToUse)}
                    {settings?.showMostReadViews === false ? null : ` • ${article._viewsToUse.toLocaleString()} Views`}
                  </p>
                </div>
              </Link>
            ));
          })()}
        </div>
      </div>

      {/* Ad After Most Visited */}
      <AdSlotWrapper settings={settings} id="homepage_sidebar_5" style={{ minHeight: '250px', marginBottom: '24px' }} className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded" />

      {/* From Our Partners */}
      <div className="mt-2 mb-6">
        <h3 className="text-2xl font-bold mb-4 uppercase text-brand-text border-b border-gray-300 pb-2">
          From Our Partners
        </h3>
        <AdSlotWrapper settings={settings} id="taboola_sidebar" className="w-full bg-brand-surface-alt flex items-center justify-center text-brand-muted font-bold rounded" style={{ minHeight: '300px' }} />
      </div>

    </aside>
  );
};

export default RightSidebar;
